import { spawn as nodeSpawn, type ChildProcessWithoutNullStreams } from 'node:child_process';
import { mkdtempSync, rmSync, writeFileSync } from 'node:fs';
import { tmpdir } from 'node:os';
import { join } from 'node:path';
import { StudioEventBus, type StudioServeEvent, type StudioTargetKind } from './studio-events.js';
import {
  describeEndpointForMessage,
  normalizeLocalUpstream,
  startStudioProxy,
  type RunningStudioProxy,
  type StudioProxyConfig,
} from './studio-proxy.js';
import { buildSharedChildArgs, type SharedChildConfig } from './studio-child-args.js';
import { buildPerRunArgs, resolveEnvVars, type OptionValues } from './studio-option-specs.js';
import { buildCatalogArgs, tokenizeRawArgs, type CatalogValues } from './studio-option-catalog.js';
import { getLogger } from '../utils/logger.js';

/** A request to start serving a target, as the studio UI posts it. */
export interface StudioServeRequest {
  /** Target id the user picked (display path or stack-qualified id). */
  targetId: string;
  /** Target kind — a serve kind (`api` / `alb` / `ecs`); see SERVE_SPECS. */
  kind: StudioTargetKind;
  /** Per-run option values (issue #301 slice 2), keyed by option flag. */
  options?: OptionValues;
  /**
   * Auto-rendered "All options" control values, keyed by long flag. Built into
   * argv by {@link buildCatalogArgs} and appended after the curated per-run
   * args (before {@link rawArgs}), so every non-curated serve flag is reachable
   * through a real control instead of only the raw extra-args input.
   */
  catalogArgs?: CatalogValues;
  /**
   * Raw extra args from the "All options" section — tokenized (quote-aware)
   * and appended verbatim to the spawned serve child, so a flag the curated
   * controls don't expose can still be passed.
   */
  rawArgs?: string;
  /**
   * Dockerfile path picked for a pinned `ecs` service (issue #301) — appended
   * as `--image-override <path>` so `start-service` rebuilds the
   * deployed-registry-pinned image from local source. Bare (picker) form: the
   * single booted service is the override target. Ignored when blank.
   */
  imageOverride?: string;
  /**
   * Per-backing-service Dockerfile paths for an `alb` serve (issue #384), keyed
   * by the backing service's `Stack:LogicalId` (the `--image-override` key
   * `start-alb` matches against its own service-boot target). One
   * `--image-override <service>=<dockerfile>` is appended per entry so the
   * ALB's pinned backing services rebuild from local source. Ignored when empty.
   */
  imageOverrides?: Record<string, string>;
}

/** A request to stop a running served target. */
export interface StudioStopRequest {
  /** Target id of the serve to stop. */
  targetId: string;
}

/** The public state of one running served target (returned to the UI). */
export interface StudioServeState {
  /** Target id of the served target. */
  targetId: string;
  /** The served target's kind. */
  kind: StudioTargetKind;
  /** Lifecycle status. */
  status: 'starting' | 'running' | 'stopped' | 'error';
  /** Served endpoint URLs, populated once running. */
  endpoints: string[];
  /**
   * Direct host URL for an `ecs` serve published via `--host-port` (issue
   * #322). Unlike `endpoints` (api / alb capture-proxy URLs), this is the
   * replica's own host port with NO proxy in front — so the in-workspace
   * request composer can target it. A request relayed through the composer
   * still lands on the timeline (studio emits the invocation events itself),
   * but an EXTERNAL curl straight to the host port is not captured (no proxy
   * intercepts it). Absent for api / alb (use `endpoints`) and for an ecs
   * serve started without `--host-port`.
   */
  hostUrl?: string;
  /** Child process id. */
  pid?: number;
  /** Wall-clock epoch ms when the serve started. */
  startedAt: number;
}

/** Config for {@link createStudioServeManager}. */
export interface StudioServeManagerConfig extends SharedChildConfig {
  /** Path to the `cdkl` CLI entry (`dist/cli.js`) — usually `process.argv[1]`. */
  cliEntry: string;
  /** The shared event bus; serve + log events are emitted onto it. */
  bus: StudioEventBus;
  /**
   * When true, append `--watch` to each spawned serve child (`start-api` /
   * `start-alb` / `start-service`) so a serve started from the studio UI
   * re-synths + rolling-reloads on CDK source changes — `cdkl studio --watch`
   * (issue #301). Read per-`start()` off this (mutable) config object, so a
   * `PATCH /api/config` toggle applies to subsequently-started serves. Has NO
   * effect on single-shot invokes (the dispatcher re-synths every run anyway).
   */
  watch?: boolean;
  /** Working directory for the child serve (defaults to `process.cwd()`). */
  cwd?: string;
  /** Node binary to spawn (defaults to `process.execPath`; injectable for tests). */
  nodeBin?: string;
  /** Spawn implementation (injectable for tests). */
  spawnFn?: typeof nodeSpawn;
  /** Clock (injectable for tests; defaults to `Date.now`). */
  clock?: () => number;
  /**
   * Max ms to wait for the first `Server listening on ...` line before
   * giving up and killing the child. Generous by default to cover the
   * first-run base-image pull `start-api` triggers. Defaults to 120_000.
   */
  readyTimeoutMs?: number;
  /**
   * Ms to wait for a SIGTERM'd child to exit before sending SIGKILL.
   * Generous by default so a serve command running its OWN teardown
   * completes first — `start-alb` / `start-service` drain + remove their
   * ECS replicas + the shared docker network, which can take well over
   * 10s; a premature SIGKILL would orphan those containers/network.
   * Defaults to 45_000.
   */
  stopGraceMs?: number;
  /** `setTimeout` (injectable for tests). */
  setTimeoutFn?: typeof setTimeout;
  /** `clearTimeout` (injectable for tests). */
  clearTimeoutFn?: typeof clearTimeout;
  /**
   * Factory for the capture proxy fronting each HTTP serve endpoint
   * (slice C2). Injectable for tests; defaults to {@link startStudioProxy}.
   * When `captureRequests` is false this is never called.
   */
  proxyFactory?: (config: StudioProxyConfig) => Promise<RunningStudioProxy>;
  /**
   * Front each HTTP serve endpoint with a capture proxy so every request
   * to the served port lands on the studio timeline (decision D4a).
   * Defaults to true; set false to hand the child's port through
   * unproxied (the slice C1 behavior).
   */
  captureRequests?: boolean;
}

/** A bound serve manager exposing the start / stop / list surface. */
export interface StudioServeManager {
  /** Start serving a target; resolves once it is listening (or rejects). */
  start: (req: StudioServeRequest) => Promise<StudioServeState>;
  /** Stop a running served target; resolves once the child has exited. */
  stop: (req: StudioStopRequest) => Promise<void>;
  /** Snapshot of every running served target. */
  list: () => StudioServeState[];
  /** Stop every running served target (graceful shutdown). */
  stopAll: () => Promise<void>;
}

/** How the serve manager drives one long-running serve kind. */
interface ServeKindSpec {
  /** Headless subcommand spawned (e.g. `start-api`). */
  command: string;
  /** Port / host flags this command accepts (start-api binds an HTTP port). */
  portArgs: readonly string[];
  /**
   * The stdout line that signals readiness. Capture group 1, when present,
   * is the served endpoint URL (api / alb); a kind with no host endpoint
   * (ecs service — pure compute) matches with NO capture group.
   *
   * ANCHORED to the start of the line (issue #578). Every serve command emits
   * its ready banner as a WHOLE line, so an unanchored pattern only ever
   * ADDED matches — a phrase embedded mid-message, in cdk-local's own relayed
   * diagnostics or in the served application's own output (`Server listening
   * on http://0.0.0.0:3000` is an ordinary thing for a web framework in an
   * ECS replica to print). Matched against the DECORATION-STRIPPED line
   * ({@link classifyChildLine}), so `--verbose`'s timestamp + level preamble
   * and a child logger's `[module]` tag do not break the anchor.
   */
  readyRe: RegExp;
  /** Front each HTTP endpoint with a capture proxy (api / alb yes, ecs no). */
  capturesHttp: boolean;
  /**
   * An ADDITIONAL endpoint line (capture group 1 = URL) emitted alongside the
   * `readyRe` line. Used by the `agentcore-ws` serve, which prints both `Server
   * listening on ws://...` (the readiness signal + WebSocket-console endpoint)
   * AND `HTTP contract served on http://...` (the warm container's HTTP
   * contract): the ws:// endpoint passes straight through for the console while
   * the http:// endpoint is fronted by the capture proxy so `/invocations`
   * requests land on the timeline. Optional — only `agentcore-ws` sets it.
   * Anchored + decoration-stripped exactly like {@link readyRe}.
   */
  extraEndpointRe?: RegExp;
  /**
   * True when this kind legitimately prints its {@link readyRe} banner MORE
   * THAN ONCE, so the post-ready stop below must NOT apply to it (issue #578).
   *
   * Exactly two kinds do, and both were verified at the emit site rather than
   * assumed:
   *
   *   - `api` — `local-start-api` writes one banner per API GROUP and one per
   *     WebSocket API (`process.stdout.write`, two consecutive loops).
   *   - `alb` — `buildFrontDoor` logs one per LISTENER, and the loop `await`s
   *     `startFrontDoorServer` between them, so those banners land in
   *     DIFFERENT stdout chunks and therefore on different ticks. A
   *     settle-based stop would silently drop every listener after the first.
   *
   * The other four print theirs once: `cloudfront` (one distribution per
   * invocation), `agentcore-ws` (one listen line, either `ws://` or `http://`),
   * and `ecs` / `ecs-task`, whose banners carry no capture group at all.
   */
  readyRepeats?: boolean;
}

/**
 * The serve lifecycle per kind. `api` + `alb` expose host HTTP endpoints
 * the studio capture proxy fronts; `ecs` (start-service) is pure compute
 * with no host port, so it has no endpoint and no capture — studio just
 * runs the replicas + streams their logs.
 */
const SERVE_SPECS: Partial<Record<StudioTargetKind, ServeKindSpec>> = {
  api: {
    command: 'start-api',
    portArgs: ['--port', '0', '--host', '127.0.0.1'],
    readyRe: /^Server listening on (\S+)/,
    capturesHttp: true,
    // One banner per API group + one per WebSocket API.
    readyRepeats: true,
  },
  alb: {
    // start-alb binds the deployed listener ports directly (no --port);
    // its front-door line carries the bound URL. Anchor the capture on a
    // URL scheme: under --tls start-alb also logs `ALB front-door:
    // generated self-signed cert at ...`, which a bare `(\S+)` would match
    // (capturing `generated`) and flip the serve to running prematurely.
    command: 'start-alb',
    portArgs: [],
    readyRe: /^ALB front-door: (https?:\/\/\S+)/,
    capturesHttp: true,
    // One banner per listener, and the loop awaits between them.
    readyRepeats: true,
  },
  ecs: {
    // start-service runs the service replicas only — no host port, no
    // capture. `Service(s) running:` is its stable ready marker.
    command: 'start-service',
    portArgs: [],
    readyRe: /^Service\(s\) running:/,
    capturesHttp: false,
  },
  'ecs-task': {
    // run-task runs a task definition's containers once (issue #366). For a
    // server task def the containers stream logs until stopped (the serve
    // lifecycle); a batch task exits and the run flips to stopped. No host
    // port, no capture. `Task running (family=...)` (the run-task onReady
    // banner) is its stable ready marker.
    command: 'run-task',
    portArgs: [],
    readyRe: /^Task running \(family=/,
    capturesHttp: false,
  },
  cloudfront: {
    // start-cloudfront serves the distribution's S3 origin + CloudFront
    // Functions over a local HTTP port (issue #363 / #367). It binds an
    // OS-assigned port with --port 0 and logs `CloudFront distribution
    // serving on <url>`; front it with a capture proxy like api / alb so
    // requests land on the timeline.
    command: 'start-cloudfront',
    portArgs: ['--port', '0', '--host', '127.0.0.1'],
    readyRe: /^CloudFront distribution serving on (https?:\/\/\S+)/,
    capturesHttp: true,
  },
  'agentcore-ws': {
    // start-agentcore boots an AgentCore Runtime container ONCE, keeps it warm,
    // and serves its native protocol contract on one host port (issue #454). It
    // binds an OS-assigned port with --port 0 and prints `Server listening on
    // <url>` — `ws://...` for HTTP / AGUI (which ALSO serve the /ws bridge),
    // `http://...` for MCP / A2A. readyRe captures whichever scheme so all four
    // protocols flip to running. capturesHttp is TRUE: a ws:// endpoint passes
    // straight through (the capture-proxy gate is /^https?:/) for the WebSocket
    // console, while an http:// endpoint is fronted by the capture proxy so
    // contract requests land on the timeline. For HTTP / AGUI the listen line
    // is ws://, so the http:// contract endpoint arrives on a SECOND line
    // (`HTTP contract served on http://...`) — extraEndpointRe captures it.
    command: 'start-agentcore',
    portArgs: ['--port', '0', '--host', '127.0.0.1'],
    readyRe: /^Server listening on (\S+)/,
    capturesHttp: true,
    extraEndpointRe: /^HTTP contract served on (https?:\/\/\S+)/,
  },
};

/**
 * The `ecs-task-runner` replica publish banner, with its authority captured.
 * The authority is a dotted-quad IPv4 or a bracketed IPv6 literal, each
 * followed by `:<port>`.
 */
const PUBLISHED_ENDPOINT_RE =
  /^Container '[^']*' container port \d+ published on ((?:\d{1,3}(?:\.\d{1,3}){3}|\[[0-9A-Fa-f:.]{2,45}\]):\d+)/;

/**
 * Parse an auto-published replica host endpoint from an `ecs` serve child's
 * stdout (issue #392). `start-service` publishes each replica's declared
 * container port on the host — auto-remapping a privileged port (< 1024) to a
 * free high port (issue #357) — and logs the WHOLE line
 * `Container 'web' container port 80 published on 127.0.0.1:54321. Reach it at
 * 127.0.0.1:54321.` (`ecs-task-runner`). studio surfaces the FIRST such
 * endpoint as the serve's `hostUrl` so the in-workspace request composer can
 * target it even when the user passed no explicit `--host-port`. Returns
 * `http://<ip>:<port>` or `undefined` when the line is not that banner.
 * Exported for unit testing.
 *
 * ANCHORED to the whole banner (issue #578), not just the `published on`
 * phrase: `hostUrl` is a destination the request composer posts to DIRECTLY,
 * with no proxy in between, so a mid-message occurrence of the phrase — in a
 * relayed error, or in a replica's own application output — must not be able
 * to name it. Pass the decoration-stripped line ({@link classifyChildLine}).
 * The loopback bound is applied by the CALLER (a parsed endpoint is still only
 * adopted when {@link normalizeLocalUpstream} accepts it), so this stays a pure
 * reader of the banner.
 *
 * The authority half accepts a BRACKETED IPv6 literal alongside the dotted
 * quad (issue go-to-k/cdk-local#599). `ecs-task-runner` composes that banner
 * through `formatAuthority`, so `--container-host ::1` prints
 * `[::1]:54321` — an emitter that brackets and a reader that only knows IPv4
 * is the same defect moved one file over. Brackets are REQUIRED here rather
 * than optional: a bare `::1:54321` has no unambiguous split into host and
 * port, and it is not what the emitter writes.
 */
export function parsePublishedHostEndpoint(line: string): string | undefined {
  const m = PUBLISHED_ENDPOINT_RE.exec(line);
  return m ? `http://${m[1]}` : undefined;
}

/**
 * The one wording for "studio will not send requests to that destination"
 * (issue #578), shared by every site that resolves a relay target: the ready
 * line, the agentcore extra-endpoint line, the ecs replica publish banner, and
 * the `--host-port` mapping. `endpoint` is untrusted text (child stdout, or a
 * user-supplied flag value), so it is flattened + length-capped before being
 * quoted back.
 */
function describeForeignEndpointRefusal(targetId: string, endpoint: string, what: string): string {
  const quoted = `'${describeEndpointForMessage(endpoint)}'`;
  const outcome = 'No endpoint was adopted and no capture proxy was started.';
  // Two different faults reach this one refusal, and calling both
  // "non-loopback" misnames the commoner one: `--host-port 80=abc` builds
  // `http://127.0.0.1:abc`, which `new URL` rejects for its port — a value
  // that names loopback and is refused for not parsing at all. So does
  // `http://:::8080` (an unbracketed IPv6 authority), which now also tears the
  // whole serve down, making the wrong reason more expensive to misread.
  if (!parsesAsUrl(endpoint)) {
    return (
      `refused an unparseable ${what} ${quoted} from '${targetId}': it is not a URL, ` +
      `so studio cannot tell where a request to it would go. ${outcome}`
    );
  }
  return (
    `refused a non-loopback ${what} ${quoted} from ` +
    `'${targetId}': a serve child always listens on this machine, so studio will ` +
    `not send requests there. ${outcome}`
  );
}

/** True when `value` is a URL the WHATWG parser accepts. */
function parsesAsUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

/** ANSI SGR colour escapes, as the logger wraps a warn / error line with. */
// eslint-disable-next-line no-control-regex -- ESC is exactly what is being stripped.
const ANSI_SGR_RE = /\u001b\[[0-9;]*m/g;
/** The verbose-mode preamble `<iso-timestamp> <LEVEL>  ` (`utils/logger`). */
const VERBOSE_PREAMBLE_RE = /^\d{4}-\d{2}-\d{2}T[\d:.]+Z\s+(DEBUG|INFO|WARN|ERROR)\s+/;
/**
 * A child logger's `[module] ` tag (`utils/logger`'s `ChildLogger`).
 *
 * Stripped ONLY after a verbose preamble, and ONLY for the ecs publish banner
 * ({@link ClassifiedChildLine.untagged}) — never for the ready-banner match.
 * `ChildLogger` emits the tag exclusively at `debug` level (`logger.ts`
 * `info` / `warn` / `error` prefix only when `getLevel() === 'debug'`), which
 * is also the level that renders the `<timestamp> <LEVEL>` preamble, so a BARE
 * `[tag] ` at column 0 is never cdk-local's. It IS how relayed third-party
 * container output arrives — `[<container>] ` (`ecs-task-runner`) and
 * `[svc=... r=... c=...] ` (`ecs-service-runner`) — so stripping it
 * unconditionally re-anchored an application's own log line to `^` and let it
 * impersonate a ready banner (issue #578).
 */
const MODULE_TAG_RE = /^\[[^\]]*\]\s+/;
/** The compact-mode level prefix `WARN: ` / `ERROR: ` (`utils/logger`). */
const COMPACT_LEVEL_RE = /^(WARN|ERROR):\s/;

/** One child stdout line, with cdk-local's own log decoration read off it. */
export interface ClassifiedChildLine {
  /**
   * True when the line is one of cdk-local's OWN warn / error diagnostics.
   * Such a line is never a ready banner, and it is the one place a
   * wire-derived string (an AWS SDK error `message`) is relayed onto the
   * child's stdout — so no endpoint is ever read out of it (issue #578).
   */
  diagnostic: boolean;
  /**
   * The line with ANSI colour, the verbose `<timestamp> <LEVEL>` preamble and
   * the compact `WARN: ` / `ERROR: ` prefix removed — so a ready pattern can
   * be anchored to `^` regardless of which of the logger's two rendering modes
   * the child ran in. A `[module]` tag is deliberately NOT removed here: no
   * ready banner is ever tag-prefixed (`start-api` / `start-cloudfront` use
   * `process.stdout.write`; `start-alb` / `run-task` / `start-agentcore` use
   * the ROOT logger), while relayed container output IS, so stripping it would
   * hand a third-party log line the `^` anchor. Leading whitespace is NOT
   * trimmed either: the banners start at column 0, and trimming would let an
   * indented line impersonate one.
   */
  text: string;
  /**
   * {@link text} with one leading `[module] ` tag additionally removed, and
   * ONLY when a verbose preamble preceded it — the single shape a cdk-local
   * child logger can emit. Used by exactly one caller: the ecs publish banner
   * ({@link parsePublishedHostEndpoint}), the one banner written through
   * `getLogger().child('ecs')`. The ready-banner match reads {@link text}.
   */
  untagged: string;
}

/**
 * Read the log decoration off one child stdout line (issue #578).
 *
 * `cdkl studio` runs its serve children with `CDKL_LOG_STREAM=stdout` (issue
 * #403), so cdk-local's own warn / error lines share the stream the ready
 * banners arrive on. Those lines relay wire-derived text (an AWS SDK error's
 * `message`), which is exactly the text that must never be able to name the
 * endpoint studio proxies to. The compact renderer prefixes them `WARN: ` /
 * `ERROR: ` (the same signal `studio-ui`'s `logLineClass` colours off) and the
 * verbose renderer (`--verbose` / `CDKL_LOG_LEVEL=debug`, both reachable in a
 * studio-spawned child) writes `<timestamp> <LEVEL>  [module] `. Both shapes
 * are recognised here, so the caller can skip a diagnostic line AND anchor its
 * patterns against `text` without the mode deciding whether the anchor holds.
 */
export function classifyChildLine(line: string): ClassifiedChildLine {
  let text = line.replace(ANSI_SGR_RE, '');
  let diagnostic = false;
  const verbose = VERBOSE_PREAMBLE_RE.exec(text);
  if (verbose) {
    if (verbose[1] === 'WARN' || verbose[1] === 'ERROR') diagnostic = true;
    text = text.slice(verbose[0].length);
  }
  const level = COMPACT_LEVEL_RE.exec(text);
  if (level) {
    diagnostic = true;
    text = text.slice(level[0].length);
  }
  // The module tag is peeled into a SEPARATE field, and only behind a verbose
  // preamble — see {@link MODULE_TAG_RE}. A bare `[tag] ` at column 0 is
  // relayed container output, so `text` keeps it and no ready pattern anchors.
  let untagged = text;
  if (verbose) {
    const tag = MODULE_TAG_RE.exec(text);
    if (tag) untagged = text.slice(tag[0].length);
  }
  return { diagnostic, text, untagged };
}

interface ServeEntry extends StudioServeState {
  child: ChildProcessWithoutNullStreams;
  /**
   * Capture proxies fronting this serve's HTTP endpoints (slice C2), keyed by
   * the RESOLVED upstream each one forwards to.
   *
   * A map rather than a list, because `api` / `alb` are exempt from the
   * post-settle `readyRe` stop (see {@link ServeKindSpec.readyRepeats}) and so
   * can match the banner any number of times. Starting a fresh proxy per match
   * bound the socket count to the child's OUTPUT: a handler behind `start-api`
   * logging `Server listening on http://127.0.0.1:9999` once per invocation
   * gave N listening sockets and N UI endpoints, released only at serve stop.
   * The endpoint dedup downstream could never catch it — every proxy carries a
   * fresh port, so `proxy.url` differs each time. Keying by upstream reuses the
   * proxy already fronting that destination, while a genuinely NEW upstream
   * (the multi-listener `alb` case) still gets its own.
   *
   * The value is the PENDING promise, not the settled proxy: two identical
   * ready lines in one stdout chunk both reach this map before either
   * `proxyFactory` call resolves, so caching the promise is what makes the
   * second one a reuse instead of a race.
   */
  proxies: Map<string, Promise<RunningStudioProxy>>;
  /**
   * Temp dir holding the `--env-vars` SAM-shape file (issue #355), when the
   * serve was started with env-var overrides. Removed on teardown (the file
   * must outlive a `--watch` serve's reloads, which re-read it, so it is
   * cleaned up only when the serve stops — not after spawn).
   */
  envDir?: string;
  /**
   * Set by `stop()` when it tears the child down WHILE it is still
   * `starting` (the ready promise has not settled). The child's `close`
   * handler reads it so a user-initiated stop is not misreported as a
   * boot failure (no `error` event, no `start()` reject as a crash).
   */
  stopping?: boolean;
}

/**
 * Build the studio serve manager. Slice C1 drives a long-running
 * `cdkl start-api <target>` child — studio is a control plane over the
 * CLI (the same pattern as the single-shot invoke dispatcher), so it
 * spawns the SAME headless serve command rather than re-wiring its
 * internals. This preserves byte-for-byte parity and isolates the
 * server's process-global behavior in a child.
 *
 * `start()` spawns the child with `--port 0` (OS-assigned, collision
 * free), streams its stdout/stderr onto the bus as `log` events keyed by
 * the target id, and resolves once the child prints its first
 * `Server listening on <url>` line — emitting a `serve` `running` event
 * with the discovered endpoints. `stop()` SIGTERMs the child (SIGKILL
 * after a grace window) and emits `stopped`.
 *
 * Slice C2 fronts each HTTP serve endpoint with a capture proxy
 * ({@link startStudioProxy}) so every request to the served port lands
 * on the studio timeline (decision D4a); the `endpoints` the UI is
 * handed are the proxy URLs. The serve-kinds slice generalized this to a
 * per-kind {@link ServeKindSpec}: `api` (`start-api`) + `alb`
 * (`start-alb`) expose host HTTP endpoints the proxy captures, while
 * `ecs` (`start-service`) is pure compute — no host port, no capture,
 * just the running replicas + their streamed logs.
 */
export function createStudioServeManager(config: StudioServeManagerConfig): StudioServeManager {
  const spawnFn = config.spawnFn ?? nodeSpawn;
  const nodeBin = config.nodeBin ?? process.execPath;
  const clock = config.clock ?? Date.now;
  const readyTimeoutMs = config.readyTimeoutMs ?? 120_000;
  const stopGraceMs = config.stopGraceMs ?? 45_000;
  const setTimeoutFn = config.setTimeoutFn ?? setTimeout;
  const clearTimeoutFn = config.clearTimeoutFn ?? clearTimeout;
  const cwd = config.cwd ?? process.cwd();
  const proxyFactory = config.proxyFactory ?? startStudioProxy;
  const captureRequests = config.captureRequests ?? true;

  const entries = new Map<string, ServeEntry>();

  /**
   * Release every per-serve resource fronting `e` (best-effort; idempotent):
   * its capture proxies (slice C2) and the `--env-vars` temp dir (issue #355).
   * Called at every teardown path so neither a proxy socket nor the env file
   * leaks when a serve stops / errors / times out.
   */
  async function closeProxies(e: ServeEntry): Promise<void> {
    const proxies = [...e.proxies.values()];
    e.proxies.clear();
    // Each entry is a PENDING proxy promise; await it before closing so a
    // proxy still binding when the serve tears down is not orphaned, and
    // swallow a rejected one (it never bound, so there is nothing to close).
    await Promise.all(proxies.map((p) => p.then((x) => x.close()).catch(() => undefined)));
    if (e.envDir) {
      try {
        rmSync(e.envDir, { recursive: true, force: true });
      } catch {
        /* best-effort temp cleanup — a leftover temp dir is harmless */
      }
      delete e.envDir;
    }
  }

  function publicState(e: ServeEntry): StudioServeState {
    const s: StudioServeState = {
      targetId: e.targetId,
      kind: e.kind,
      status: e.status,
      endpoints: [...e.endpoints],
      startedAt: e.startedAt,
    };
    if (e.pid !== undefined) s.pid = e.pid;
    if (e.hostUrl !== undefined) s.hostUrl = e.hostUrl;
    return s;
  }

  function emitServe(e: ServeEntry, message?: string): void {
    const ev: StudioServeEvent = {
      ts: clock(),
      target: e.targetId,
      kind: e.kind,
      status: e.status,
      endpoints: [...e.endpoints],
    };
    if (e.pid !== undefined) ev.pid = e.pid;
    if (e.hostUrl !== undefined) ev.hostUrl = e.hostUrl;
    if (message !== undefined) ev.message = message;
    config.bus.emit('serve', ev);
  }

  function buildArgs(req: StudioServeRequest, spec: ServeKindSpec, envFile?: string): string[] {
    // A `--watch` serve MUST re-synth on source changes, so it keeps
    // `--app <app>`; a non-watch serve reuses the boot-synthesized cloud
    // assembly studio captured (issue #324) and skips its own synth. Read
    // `config.watch` per start (mutable — a Session-bar toggle applies to
    // the next serve), matching the `--watch` flag append below.
    const preferAssembly = config.watch !== true;
    // Every serve kind now declares `--from-cfn-stack` / `--assume-role`:
    // start-cloudfront gained them in issue #380 (a Function URL origin Lambda
    // gets the same env / deployed-state / execution-role injection as
    // `cdkl invoke`), so the session bindings are forwarded to ALL kinds. The
    // `omitStateBindings` guard in buildSharedChildArgs stays available for a
    // future pure-local serve kind that declares neither flag.
    return [
      spec.command,
      req.targetId,
      ...spec.portArgs,
      ...buildSharedChildArgs(config, { preferAssembly }),
      ...buildPerRunArgs(req.kind, req.options),
      // Auto-rendered "All options" controls: every non-curated, non-managed
      // command flag, built into argv and validated against the flag catalog.
      ...buildCatalogArgs(req.kind, req.catalogArgs),
      // The `--env-vars` per-run option takes a FILE (issue #355) — the env-kv
      // KV rows / JSON were materialized into a SAM-shape temp file by the
      // caller; point start-service / start-alb at it so the override applies
      // to the backing ECS task containers.
      ...(envFile ? ['--env-vars', envFile] : []),
      // Image-override picker (issue #301): a pinned ecs service rebuilds from
      // the chosen local Dockerfile. The EXPLICIT `<target>=<dockerfile>` form
      // is used (not the bare picker form) because studio spawns the child
      // WITHOUT a TTY — the engine skips bare picker-form paths when
      // non-interactive, but the explicit form maps deterministically. The
      // target key is the same id passed as the start-service target arg.
      ...(req.imageOverride && req.imageOverride.trim() !== ''
        ? ['--image-override', req.targetId + '=' + req.imageOverride.trim()]
        : []),
      // Per-backing-service image-override for an `alb` serve (issue #384): an
      // ALB boots multiple backing ECS services, so the alb composer's per-
      // service pickers thread one explicit `--image-override <service>=<df>`
      // each. The service key is the backing service's `Stack:LogicalId` —
      // exactly start-alb's own service-boot target — so the override engine
      // matches it without an orphan-flag failure.
      ...Object.entries(req.imageOverrides ?? {}).flatMap(([svc, df]) =>
        df && df.trim() !== '' ? ['--image-override', svc + '=' + df.trim()] : []
      ),
      // `cdkl studio --watch` (issue #301): every serve kind (start-api /
      // start-alb / start-service) implements `--watch` rolling reload, so
      // forwarding the bare flag makes a UI-started serve hot-reload on source
      // changes. Read off the (mutable) config per start so a Session-bar
      // toggle applies to the next serve.
      ...(config.watch === true ? ['--watch'] : []),
      // Raw extra args go LAST so a user can override an earlier flag if they
      // mean to (Commander takes the last value for a scalar flag).
      ...tokenizeRawArgs(req.rawArgs),
    ];
  }

  async function start(req: StudioServeRequest): Promise<StudioServeState> {
    const spec = SERVE_SPECS[req.kind];
    if (!spec) {
      throw new Error(
        `Serving '${req.kind}' targets from studio is not supported (serve kinds: ${Object.keys(SERVE_SPECS).join(', ')}).`
      );
    }
    const existing = entries.get(req.targetId);
    if (existing && existing.status !== 'stopped' && existing.status !== 'error') {
      throw new Error(`'${req.targetId}' is already running.`);
    }

    const startedAt = clock();

    // Materialize the `--env-vars` env-kv option (issue #355) into a SAM-shape
    // temp file the serve child reads. Computed BEFORE spawn so a malformed
    // JSON value throws as a clean /api/run boundary error rather than after a
    // child is already running. The dir outlives the child (a `--watch` serve
    // re-reads the file on reload) and is removed on teardown via closeProxies.
    let envDir: string | undefined;
    const envVars = resolveEnvVars(req.kind, req.options);
    if (envVars) {
      envDir = mkdtempSync(join(tmpdir(), 'cdkl-studio-env-'));
      writeFileSync(join(envDir, 'env-vars.json'), JSON.stringify(envVars));
    }
    const envFile = envDir ? join(envDir, 'env-vars.json') : undefined;

    let child: ChildProcessWithoutNullStreams;
    try {
      // `CDKL_LOG_STREAM=stdout` (issue #403): route the serve child's warn /
      // error to stdout too, so its boot diagnostics do not race ahead of
      // stdout lines across two OS pipes in the studio LOG panel (Node does not
      // guarantee cross-pipe delivery order, so a stderr WARN could otherwise
      // surface AFTER a later stdout banner like "Press ^C to shut down."). The
      // serve path is safe: ready-line detection greps stdout for specific
      // patterns and error detection is via the child `error` / `close` events,
      // not stderr content. (NOT applied to single-shot invoke children, whose
      // agentcore path treats whole stdout as the response.)
      child = spawnFn(nodeBin, [config.cliEntry, ...buildArgs(req, spec, envFile)], {
        cwd,
        env: { ...process.env, CDKL_LOG_STREAM: 'stdout' },
      });
    } catch (err) {
      // Spawn never happened — drop the env temp dir so it does not leak.
      if (envDir) {
        try {
          rmSync(envDir, { recursive: true, force: true });
        } catch {
          /* best-effort */
        }
      }
      throw err instanceof Error ? err : new Error(String(err));
    }

    const entry: ServeEntry = {
      targetId: req.targetId,
      kind: req.kind,
      status: 'starting',
      endpoints: [],
      startedAt,
      child,
      proxies: new Map(),
    };
    if (child.pid !== undefined) entry.pid = child.pid;
    // Bind the env temp dir to the entry so closeProxies removes it on teardown.
    if (envDir) entry.envDir = envDir;
    // An ecs serve published via `--host-port` is reachable on the host (issue
    // #322); surface its host URL so the in-workspace request composer can
    // target it (the first mapping's host port). No proxy fronts it, so a
    // request to it is not captured on the timeline.
    if (req.kind === 'ecs') {
      const hp = req.options?.['--host-port'];
      if (Array.isArray(hp)) {
        const first = hp.find(
          (r) => r && typeof r === 'object' && typeof r.right === 'string' && r.right.trim() !== ''
        );
        if (first) {
          // Issue #578 review — the ONE relay destination that used to skip
          // the loopback bound. The port half is interpolated verbatim, so
          // `--host-port 80=1@evil.example` yields `http://127.0.0.1:1@evil.
          // example`, whose HOST is `evil.example`. It is user-supplied rather
          // than child-supplied, but it is the same destination the composer
          // posts to directly, so it goes through the same resolution.
          const candidate = 'http://127.0.0.1:' + first.right.trim();
          const local = normalizeLocalUpstream(candidate);
          if (local === undefined) {
            const msg = describeForeignEndpointRefusal(
              req.targetId,
              candidate,
              '--host-port mapping'
            );
            emitLog(config.bus, clock, req.targetId, `WARN: ${msg}`, 'stderr');
            getLogger().warn(msg);
          } else {
            entry.hostUrl = local;
          }
        }
      }
    }
    entries.set(req.targetId, entry);
    emitServe(entry);

    return new Promise<StudioServeState>((resolve, reject) => {
      let settled = false;
      child.stdout.setEncoding('utf8');
      child.stderr.setEncoding('utf8');

      const timer = setTimeoutFn(() => {
        // `entry.stopping` as well as `settled`, matching `failServe`. Without
        // it a user `stop()` during boot produces `starting -> error -> stopped`
        // -- an error banner for a serve they deliberately stopped, the exact
        // symptom `failServe` guards against one screen below.
        //
        // The `entries.delete` below is the sharper reason: it is keyed by
        // TARGET, not by this entry, so if the same target was restarted inside
        // the window this stale timer evicts the NEW, running entry. Its child
        // then has no record in `entries`, so it can never be stopped and its
        // proxy is never closed, while this branch SIGTERMs the old child.
        if (settled || entry.stopping) return;
        settled = true;
        entry.status = 'error';
        emitServe(entry, `Timed out after ${readyTimeoutMs}ms waiting for the serve to be ready.`);
        // Graceful SIGTERM -> SIGKILL (NOT an immediate SIGKILL): the serve
        // commands trap SIGTERM to tear down their containers, so a hard kill
        // on a half-booted child would orphan those containers.
        void stopChild(child, stopGraceMs, setTimeoutFn, clearTimeoutFn);
        void closeProxies(entry);
        if (entries.get(req.targetId) === entry) entries.delete(req.targetId);
        reject(new Error(`'${req.targetId}' did not start within ${readyTimeoutMs}ms.`));
      }, readyTimeoutMs);
      timer.unref?.();

      const becomeRunning = (): void => {
        if (settled) return;
        settled = true;
        clearTimeoutFn(timer);
        entry.status = 'running';
        emitServe(entry);
        resolve(publicState(entry));
      };

      /**
       * Refuse a destination a child's stdout named that is not on this
       * machine (issue #578), and say so loudly: on the bus, so the studio
       * LOGS panel shows it (rendered with the same `WARN: ` prefix the
       * compact logger emits, which `studio-ui` colours), and on the studio
       * process's own logger, for a user watching the terminal. The endpoint
       * is untrusted text, so it is flattened + length-capped before being
       * quoted back.
       */
      const refuseForeignEndpoint = (endpoint: string, what: string): string => {
        const msg = describeForeignEndpointRefusal(req.targetId, endpoint, what);
        emitLog(config.bus, clock, req.targetId, `WARN: ${msg}`, 'stderr');
        getLogger().warn(msg);
        return msg;
      };

      /**
       * Fail the serve NOW with `reason`, instead of leaving it `starting`
       * until `readyTimeoutMs` expires (issue #578 review).
       *
       * A refused ready line is terminal: the child named the only endpoint it
       * is going to name, and studio will not use it. Waiting out the default
       * 120 s and then reporting `did not start within 120000ms` buries the
       * real cause in the LOGS panel, and it is a live configuration rather
       * than an attack that lands here — `cdkl start-alb --container-host
       * <non-loopback>` makes the front-door banner the refused line. Mirrors
       * the timeout path exactly (graceful SIGTERM so the child tears its own
       * containers down, proxies closed, entry dropped) but carries the
       * refusal text as the failure reason, so the UI error banner says why.
       *
       * The trade: a serve that would have recovered — a foreign-looking line
       * arriving BEFORE the genuine banner — now dies instead of waiting. That
       * line has to clear bound 1 (not a WARN / ERROR), bound 2 (column 0, and
       * as of this change not tag-stripped) and still name a non-loopback,
       * non-wildcard host, which is the misconfiguration case far more than
       * the coincidence case; and the old outcome for it was a 120 s wait
       * ending in the same failure with a worse message.
       */
      const failServe = (reason: string): void => {
        // `entry.stopping` as well as `settled`, mirroring the `close` handler
        // below: `stop()` marks the entry, drops it from the map and then
        // awaits `stopChild` for up to `stopGraceMs` (45 s — an ALB teardown
        // really takes that long). A refused ready line arriving inside that
        // window used to emit an `error` serve event and reject `start()`,
        // giving the UI a failure banner for a serve the user deliberately
        // stopped, immediately followed by the `stopped` event.
        if (settled || entry.stopping) return;
        settled = true;
        clearTimeoutFn(timer);
        entry.status = 'error';
        emitServe(entry, reason);
        void stopChild(child, stopGraceMs, setTimeoutFn, clearTimeoutFn);
        void closeProxies(entry);
        entries.delete(req.targetId);
        reject(new Error(reason));
      };

      // Handle a child ready line. `childUrl` (capture group 1) is the
      // served endpoint for api / alb — fronted with a capture proxy (slice
      // C2 / decision D4a) when `capturesHttp` so every request to it lands
      // on the timeline; ws:// (and capture-off) endpoints pass straight
      // through. An ecs service has NO endpoint (`childUrl` undefined): it
      // just flips to running with no endpoint + no proxy. The FIRST ready
      // line flips the serve to running; later ones append + re-emit.
      const onReady = async (readyUrl?: string): Promise<void> => {
        // Issue #578 — the bound that does not depend on getting the ready
        // pattern exactly right: a serve child always listens on THIS machine,
        // so an endpoint naming any other host did not come from a genuine
        // ready banner. Refuse it outright — no proxy, no adopted endpoint, and
        // no flip to running, since flipping would publish a foreign URL to the
        // UI as this serve's endpoint. The serve stays `starting` and the ready
        // timeout reports it honestly; the warn says why. A WILDCARD bind
        // address (`--container-host 0.0.0.0`, or an `api` / `alb` bound to
        // `::`) is a legitimate serve that IS reachable on loopback, so it is
        // rewritten to `127.0.0.1` — and the REWRITTEN url is what is adopted
        // as the endpoint and proxied to, so the composer targets a real
        // destination rather than a bind address.
        let childUrl = readyUrl;
        if (readyUrl !== undefined) {
          const local = normalizeLocalUpstream(readyUrl);
          if (local === undefined) {
            const msg = refuseForeignEndpoint(readyUrl, 'ready line');
            // Terminal when the serve has no endpoint yet: settle NOW with the
            // refusal as the reason rather than hanging until the ready
            // timeout. Once running (an agentcore extra-endpoint line, a later
            // alb listener) the serve keeps serving what it already has.
            failServe(msg);
            return;
          }
          childUrl = local;
        }
        let endpoint = childUrl;
        if (childUrl && spec.capturesHttp && captureRequests && /^https?:/i.test(childUrl)) {
          try {
            // Reuse the proxy already fronting this exact upstream rather than
            // starting another (see `ServeEntry.proxies`). Registered BEFORE
            // the await so a second identical ready line in the same chunk
            // joins this startup instead of racing a duplicate one.
            let pending = entry.proxies.get(childUrl);
            if (pending === undefined) {
              pending = proxyFactory({
                bus: config.bus,
                target: req.targetId,
                kind: req.kind,
                upstream: childUrl,
              });
              entry.proxies.set(childUrl, pending);
            }
            const proxy = await pending;
            endpoint = proxy.url;
          } catch {
            // A failed startup is not cached: drop it so the fallback below is
            // reached once per attempt rather than replaying one rejection.
            entry.proxies.delete(childUrl);
            // Proxy failed to bind — fall back to the direct child URL so the
            // serve is still usable (just uncaptured). Re-resolve it: the
            // value published here is a destination the composer posts to with
            // no proxy in front, and `startStudioProxy` enforces the same
            // loopback bound by THROWING, so a caller that lost the guard
            // above would otherwise have its refusal caught here and the
            // foreign URL handed to the UI anyway (issue #578, defence in
            // depth). Idempotent on the normal path — `childUrl` is already
            // the resolved value.
            const direct = normalizeLocalUpstream(childUrl);
            if (direct === undefined) {
              const msg = refuseForeignEndpoint(childUrl, 'ready line');
              failServe(msg);
              return;
            }
            endpoint = direct;
          }
        }
        // A stop() may have raced the async proxy startup; if so, drop the
        // now-orphan proxy and do not resurrect the torn-down serve.
        if (entry.stopping || (settled && !entries.has(req.targetId))) {
          await closeProxies(entry);
          return;
        }
        if (endpoint && !entry.endpoints.includes(endpoint)) entry.endpoints.push(endpoint);
        if (settled) emitServe(entry);
        else becomeRunning();
      };

      streamLines(child.stdout, (line) => {
        // Issue #578 — read the log decoration off the line ONCE, then match
        // the banners against the stripped text. A WARN / ERROR line is
        // cdk-local's own diagnostic, the one place wire-derived text (an AWS
        // SDK error `message`) reaches this stream, so no endpoint is read out
        // of it at all; the line is still logged verbatim below.
        const parsed = classifyChildLine(line);
        if (!parsed.diagnostic) {
          // Issue #578 — stop reading `readyRe` once the serve has SETTLED,
          // for the kinds whose banner is a single event. The banner marks a
          // serve coming up, so a line arriving after that is not one, and
          // letting it through is how an ordinary relayed handler log —
          // `Server listening on http://127.0.0.1:9999`, at column 0 with no
          // prefix, since Lambda container output is piped through raw
          // (`docker-runner`'s `streamLogs`) — can add an endpoint and point a
          // capture proxy, carrying the developer's headers and body, at a
          // port it named. It stays on-box (the loopback bound holds), and it
          // is additive rather than a re-point (the first endpoint, the one
          // the UI shows, is never overwritten), but it is still a
          // destination the child chose after boot.
          //
          // Where the line is drawn, and why it is not drawn wider: `api` and
          // `alb` legitimately print the banner MORE THAN ONCE (see
          // `ServeKindSpec.readyRepeats` — per API group / per WebSocket API,
          // and per ALB listener with an `await` between them), so applying
          // the stop there would silently drop real endpoints. Their residual
          // is what bound 2 and bound 3 cover; closing it properly means
          // prefixing relayed CONTAINER output the way ECS already does, which
          // is `docker-runner`'s to fix, not this matcher's.
          //
          // The stop is scoped to `readyRe` alone. Two reads are legitimately
          // post-settle and stay live: `extraEndpointRe` (agentcore's `http://`
          // contract line, which by design follows the `ws://` listen line) and
          // the `ecs` publish banner (one per replica, as containers come up).
          // Both are narrower than `readyRe` and both keep the loopback bound.
          const m = spec.readyRepeats || !settled ? spec.readyRe.exec(parsed.text) : null;
          // m[1] is the endpoint URL for api / alb; undefined for ecs (no
          // capture group) — a ready line with no host endpoint.
          if (m) void onReady(m[1]);
          // An additional endpoint line (agentcore-ws: the http:// contract
          // alongside the ws:// listen line). It does NOT flip the serve to
          // running on its own (the readyRe line already did, or will) —
          // onReady appends + re-emits the endpoint, fronting it with the
          // capture proxy.
          if (spec.extraEndpointRe) {
            const me = spec.extraEndpointRe.exec(parsed.text);
            if (me) void onReady(me[1]);
          }
          // An `ecs` serve auto-publishes its replica host port(s) (issue
          // #392); surface the FIRST one as `hostUrl` so the request composer
          // fires — unless an explicit `--host-port` already set it. The
          // publish line can arrive after the ready line, so re-emit when the
          // serve is running. `hostUrl` is posted to DIRECTLY by the composer
          // (no proxy in front), so it carries the same loopback bound the
          // proxied endpoints do (issue #578).
          if (req.kind === 'ecs' && entry.hostUrl === undefined) {
            // The ONE banner a cdk-local child logger emits, so the one read
            // that may look past a `[module]` tag — and only behind a verbose
            // preamble (see `classifyChildLine`).
            const endpoint = parsePublishedHostEndpoint(parsed.untagged);
            if (endpoint) {
              // Same resolution as the ready path: a wildcard `--container-host`
              // publish is rewritten to loopback (that IS where it is
              // reachable), anything else non-loopback is refused.
              const local = normalizeLocalUpstream(endpoint);
              if (local === undefined) {
                refuseForeignEndpoint(endpoint, 'published replica endpoint');
              } else {
                entry.hostUrl = local;
                if (entry.status === 'running') emitServe(entry);
              }
            }
          }
        }
        emitLog(config.bus, clock, req.targetId, line, 'stdout');
      });
      streamLines(child.stderr, (line) => {
        emitLog(config.bus, clock, req.targetId, line, 'stderr');
      });

      child.on('error', (err) => {
        if (settled) {
          // Post-ready spawn error: mark the entry errored for the UI.
          entry.status = 'error';
          emitServe(entry, err.message);
          void closeProxies(entry);
          entries.delete(req.targetId);
          return;
        }
        settled = true;
        clearTimeoutFn(timer);
        entry.status = 'error';
        emitServe(entry, err.message);
        void closeProxies(entry);
        entries.delete(req.targetId);
        reject(err);
      });

      child.on('close', (code) => {
        if (!settled) {
          settled = true;
          clearTimeoutFn(timer);
          if (entry.stopping) {
            // A user `stop()` raced the still-starting child: not a boot
            // failure. `stop()` emits the `stopped` event + resolves itself,
            // so here we only reject the pending `start()` (the /api/run POST)
            // with an honest "cancelled" message and emit nothing.
            reject(new Error(`'${req.targetId}' was stopped before it finished starting.`));
            return;
          }
          // Exited before ever listening — a boot failure.
          entry.status = 'error';
          const msg = `Server exited before listening (code ${code ?? 'null'}).`;
          emitServe(entry, msg);
          void closeProxies(entry);
          entries.delete(req.targetId);
          reject(new Error(msg));
          return;
        }
        // Exited while running (crash or our own stop()). If still tracked
        // as running, surface it as stopped; stop() removes the entry
        // itself before this fires in the graceful path.
        const tracked = entries.get(req.targetId);
        if (tracked === entry && entry.status === 'running') {
          entry.status = 'stopped';
          emitServe(entry, `Server process exited (code ${code ?? 'null'}).`);
          void closeProxies(entry);
          entries.delete(req.targetId);
        }
      });
    });
  }

  async function stop(req: StudioStopRequest): Promise<void> {
    const entry = entries.get(req.targetId);
    if (!entry) {
      throw new Error(`'${req.targetId}' is not running.`);
    }
    // Flag so the child's `close` handler treats this as a user stop, not a
    // boot failure, when the serve was still `starting` (#1).
    entry.stopping = true;
    entries.delete(req.targetId);
    // Tear down the capture proxies and SIGTERM the child concurrently —
    // both are initiated synchronously (stopChild attaches its `close`
    // listener + sends SIGTERM in the same tick) so neither races the
    // child's exit.
    await Promise.all([
      closeProxies(entry),
      stopChild(entry.child, stopGraceMs, setTimeoutFn, clearTimeoutFn),
    ]);
    entry.status = 'stopped';
    emitServe(entry);
  }

  function list(): StudioServeState[] {
    return [...entries.values()].map(publicState);
  }

  async function stopAll(): Promise<void> {
    const targets = [...entries.keys()];
    // `stop()` itself always resolves for a tracked target (stopChild
    // escalates SIGTERM -> SIGKILL and resolves on close); the only reject
    // path is the "not running" race if a sibling stop already evicted the
    // key. The catch keeps one such race from aborting the rest of the
    // shutdown sweep.
    await Promise.all(targets.map((targetId) => stop({ targetId }).catch(() => undefined)));
  }

  return { start, stop, list, stopAll };
}

/** Emit one container log line onto the bus, keyed by the serve target id. */
function emitLog(
  bus: StudioEventBus,
  clock: () => number,
  target: string,
  line: string,
  stream: 'stdout' | 'stderr'
): void {
  bus.emit('log', { ts: clock(), containerId: target, target, line, stream });
}

/**
 * Line-buffer a child stream and invoke `onLine` per complete line
 * (trailing newline stripped, blank lines dropped). Flushes any partial
 * final line on stream end.
 */
function streamLines(stream: NodeJS.ReadableStream, onLine: (line: string) => void): void {
  let buf = '';
  stream.on('data', (chunk: string) => {
    buf += chunk;
    let nl = buf.indexOf('\n');
    while (nl !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, '');
      buf = buf.slice(nl + 1);
      if (line.length > 0) onLine(line);
      nl = buf.indexOf('\n');
    }
  });
  stream.on('end', () => {
    const line = buf.replace(/\r$/, '');
    if (line.length > 0) onLine(line);
    buf = '';
  });
}

/**
 * SIGTERM a child and resolve once it exits, escalating to SIGKILL after
 * `graceMs`. Resolves immediately if the child has already exited.
 */
function stopChild(
  child: ChildProcessWithoutNullStreams,
  graceMs: number,
  setTimeoutFn: typeof setTimeout,
  clearTimeoutFn: typeof clearTimeout
): Promise<void> {
  return new Promise<void>((resolve) => {
    let done = false;
    let kill: ReturnType<typeof setTimeout> | undefined;
    const finish = (): void => {
      if (done) return;
      done = true;
      if (kill) clearTimeoutFn(kill);
      resolve();
    };
    // Attach the `close` listener BEFORE the already-exited check so a child
    // that exits in the window between the check and the listener attach is
    // not missed (which would leave the promise pending forever). #4
    child.once('close', finish);
    if (child.exitCode !== null || child.signalCode !== null) {
      finish();
      return;
    }
    kill = setTimeoutFn(() => {
      if (!done) child.kill('SIGKILL');
    }, graceMs);
    kill.unref?.();
    child.kill('SIGTERM');
  });
}
